import { useMemo } from 'react';
import { toast } from 'sonner';
import { PERMISSION } from '@obraplan/core/domain/access/permissions';
import type { NonConformity } from '@obraplan/core/domain/quality/non-conformity';
import { asId } from '@obraplan/core/domain/shared/ids';
import { useContainer, usePermission, useSessionState } from '@/features/session/use-session';
import { ncNaFila } from '@/app/outbox-types';
import { useCommand } from '@/lib/use-command';
import { GUARDADO_NO_APARELHO } from './fila-da-conferencia';
import { placesLoader, type NcPlacesTools } from './nc-places';

/**
 * O que a tela do dia põe no contexto do passo de qualidade (Fase 37, etapa 3): o carregador dos
 * lugares daquele dia e — para quem trata NC — encerrar a NC aberta de outro dia (D10).
 *
 * `undefined` sem perfil ou sem obra: o passo funciona sem lugares e sem o aviso.
 */
export function useNcPlacesTools(
  projectId: string | undefined,
  date: string,
): NcPlacesTools | undefined {
  const container = useContainer();
  const { session } = useSessionState();
  const canManage = usePermission(
    PERMISSION.NON_CONFORMITIES_MANAGE,
    projectId === undefined ? undefined : asId<'Project'>(projectId),
  );
  // Pela fila do aparelho, como na aba Não conformidades (RN-623): sem sinal, a NC de outro dia
  // fica encerrada no aparelho e sobe na volta, na chave dela.
  const [encerrar] = useCommand((nc: NonConformity, solution: string) =>
    container.outbox.submit(
      {
        kind: 'resolver-nc',
        projectId: nc.projectId,
        date: nc.date,
        resourceId: ncNaFila(nc.id),
        nonConformityId: nc.id,
        solution,
        expectedVersion: nc.version,
      },
      session!,
    ),
  );

  return useMemo(() => {
    if (session === undefined || projectId === undefined) return undefined;
    return {
      loadPlaces: placesLoader(container, session, projectId, date),
      ...(canManage
        ? {
            resolvePending: async (nc: NonConformity, solution: string) => {
              const resultado = await encerrar(nc, solution);
              if (resultado?.ok === true) {
                toast.success(
                  resultado.value.queued
                    ? GUARDADO_NO_APARELHO
                    : `Não conformidade nº ${String(nc.number)} encerrada.`,
                );
              }
              return resultado?.ok === true;
            },
          }
        : {}),
    };
  }, [container, session, projectId, date, canManage, encerrar]);
}
